import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getInboundDocument, reprocessInboundDocument } from '../api/as2-inbound-documents';
import { useAsyncData } from '../hooks/useAsyncData';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { CopyButton } from '../components/CopyButton';
import { RelatedLinks } from '../components/RelatedLinks';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { PageHeader } from '../components/FeatureStatus';
import { PayloadViewer } from '../components/PayloadViewer';
import { as2InboundTransportByCorrelationLink, exchangesByCorrelationLink, executionsByCorrelationLink } from '../lib/crossLinks';

export function InboundMessageDetailPage() {
  const params = useParams<{ id: string }>();
  const { search } = useLocation();
  const documentId = params.id ?? '';
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const { data, isLoading, error, refresh } = useAsyncData(
    () => getInboundDocument(documentId),
    [documentId],
  );

  async function handleReprocess() {
    await reprocessInboundDocument(documentId);
    await refresh();
  }

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!data) return <EmptyState label={`Inbound message ${documentId} was not found.`} />;

  return (
    <div className="page-grid">
      <Link to={{ pathname: '/inbound-messages', search }} className="back-link">← Back to Messages</Link>
      <PageHeader
        eyebrow="Inbound messages"
        title="Inbound message detail"
        purpose="Inspect the raw envelope received via AS2, its processing outcome, and any parsed business detail, with the ability to re-trigger processing."
        status="PRODUCTION_READY"
        connected
        next="Use reprocess only after confirming the underlying issue (partner config, mapping, connectivity) has been resolved."
      />
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Inbound message detail</h2>
            <p className="mono">{data.id}</p>
          </div>
          <div className="panel-actions">
            <button type="button" onClick={() => setConfirm({
              title: 'Reprocess inbound message',
              message: 'This re-runs business payload processing (parsing, validation, downstream routing) for this inbound document.',
              confirmLabel: 'Reprocess',
              risk: 'medium',
              onConfirm: () => handleReprocess(),
            })}>
              Reprocess
            </button>
          </div>
        </div>
        <div className="summary-grid">
          <div><strong>Type</strong><span>{data.documentType}</span></div>
          <div><strong>Status</strong><span><StatusBadge value={data.processingStatus} /></span></div>
          <div><strong>Partner</strong><span>{data.partnerCode}</span></div>
          <div><strong>AS2 message ID</strong><span className="mono">{data.as2MessageId}</span></div>
          <div>
            <strong>Correlation</strong>
            <span className="inline-copy">
              <span className="mono">{data.correlationId}</span>
              <CopyButton value={data.correlationId} label="Copy correlation ID" />
              <RelatedLinks links={[
                as2InboundTransportByCorrelationLink(data.correlationId, search),
                exchangesByCorrelationLink(data.correlationId, search),
                executionsByCorrelationLink(data.correlationId, search),
              ]}
              />
            </span>
          </div>
          <div><strong>Content type</strong><span>{data.contentType}</span></div>
          <div><strong>Received</strong><span>{new Date(data.receivedAt).toLocaleString('en-GB')}</span></div>
          <div><strong>Updated</strong><span>{new Date(data.updatedAt).toLocaleString('en-GB')}</span></div>
        </div>
        {data.processingStatus === 'FAILED' && data.processingError ? (
          <div className="form-error">{data.processingError}</div>
        ) : null}
        <div className="summary-grid">
          <div><strong>Routing target</strong><span className="mono">{data.routingDecision.target}</span></div>
          <div><strong>Matched rule</strong><span>{data.routingDecision.ruleDescription}</span></div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <h2>Raw payload</h2>
          <p>{data.contentType}</p>
        </div>
        <PayloadViewer
          value={data.payload}
          contentType={data.contentType}
          filename={`${data.id}.payload.txt`}
          className="code-block content-viewer raw-payload-viewer"
        />
      </section>

      {data.cusdecMlDetail ? (
        <section className="panel">
          <div className="panel-header">
            <h2>CUSDEC ML (DUM Mainlevée) detail</h2>
            <p>This inbound message has parsed CUSDEC ML data. Full detail — operation, articles, apurement lines, seals, transport means, validation report, and raw XML — lives in the dedicated console.</p>
          </div>
          <div className="setup-callout">
            <div>
              <strong>Reference DUM {data.cusdecMlDetail.referenceDum ?? data.cusdecMlDetail.referenceMessage ?? 'n/a'}</strong>
              <span>Status: {data.cusdecMlDetail.processingStatus}</span>
            </div>
            <Link to={{ pathname: '/operations/cusdec-ml', search }}>Open in CUSDEC ML console</Link>
          </div>
        </section>
      ) : null}

      {data.dumDetail ? (
        <section className="panel">
          <div className="panel-header">
            <h2>Parsed DUM detail</h2>
            <p>Fields extracted from the ADII DUM CUSDEC payload.</p>
          </div>
          <div className="summary-grid">
            <div><strong>Flow</strong><span>{data.dumDetail.flow}</span></div>
            <div><strong>Root element</strong><span>{data.dumDetail.rootElement}</span></div>
            <div><strong>Reference DUM</strong><span className="mono">{data.dumDetail.referenceDUM}</span></div>
            <div><strong>Reference message</strong><span className="mono">{data.dumDetail.referenceMessage}</span></div>
            <div><strong>Message fonction</strong><span>{data.dumDetail.messageFonction}</span></div>
            <div><strong>Resultat ctrl</strong><span>{data.dumDetail.resultatCtrl ?? 'n/a'}</span></div>
          </div>
          <PayloadViewer value={data.dumDetail.parsedPayload} filename={`${data.dumDetail.id}.parsed-dum.json`} />
        </section>
      ) : null}

      <section className="panel">
        <div className="panel-header">
          <h2>Security summary</h2>
        </div>
        <PayloadViewer value={data.security} filename={`${data.id}.security.json`} />
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
